import React, { useEffect, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

// Internal
import { useUrlParams } from "@/hooks/useUrlParams";
import {
  AttendanceStatus,
  DayStatus,
  isAttendanceReadOnly,
} from "@/types/attendance-sheet.types";
import EasScheduleForm from "./__components/EasScheduleForm";
import EasAcknowledgements from "./__components/EasAcknowledgements";
import Container from "@/components/Container";
import {
  useGetAttendanceDetails,
  useSubmitAttendanceForFamily,
  useSubmitAttendanceForProvider,
} from "@/api/attendance";
import {
  SubmitAttendanceForFamilyDto,
  SubmitAttendanceForProviderDto,
} from "@/api/__types/attendance";
import ReadOnlyView from "./__components/EasScheduleReadyOnlyView";
import { API_ENTRYPOINT } from "@/utils/fetch";
import {
  isValidDateString,
  parseDayMonthYearString,
} from "@/utils/dates";
import GeneralErrorState from "@/components/GeneralErrorState";
import ChildActiveCertificates from "@/components/ChildActiveCertificates";

// Antd
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  DatePicker,
  Divider,
  Form,
  Layout,
  notification,
  Radio,
  Space,
  Spin,
  Tooltip,
} from "antd";
import {
  CaretLeftOutlined,
  CheckOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import { InformationFilled } from "@carbon/icons-react";

// MISC
import axios from "axios";
import dayjs from "dayjs";
import AttendanceSummary from "@/components/AttendanceSummary";

const { Footer } = Layout;

export default function Attendance() {
  const { attendanceId: selectedAttendanceSheetId } = useParams();
  const navigate = useNavigate();

  // Search params
  const [searchParams] = useSearchParams();
  const platform = searchParams.get("platform");

  const { providerId, familyId, isProviderView, isFamilyView } = useUrlParams({
    searchParams,
  });

  const {
    isLoading: attendanceSheetLoading,
    data: attendanceSheet,
    isError: attendanceSheetDetailsErrored,
    error: attendanceSheetDetailsError,
  } = useGetAttendanceDetails({
    attendanceId: selectedAttendanceSheetId || "",
    platform: platform || "",
  });

  const child = attendanceSheet?.child;

  const startDate = attendanceSheet?.periodStartDate;
  const endDate = attendanceSheet?.periodEndDate;

  // Antd form
  const [form] = Form.useForm();
  const careStartDateFormValue = Form.useWatch("careStartDate", form);
  const careStartMonthFormValue = Form.useWatch("careStartMonth", form);
  const terminatedFormValue = Form.useWatch("terminated", form);
  const attendedEntireMonthFormValue = Form.useWatch(
    "attendedEntireMonth",
    form
  );

  const billingPeriodLabel =
    startDate && endDate
      ? `${dayjs(startDate).format("MMM DD, YYYY")} - ${dayjs(endDate).format(
          "MMM DD, YYYY"
        )}`
      : "";

  // File upload
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);

  // Mutations
  const { mutate: submitAttendanceForProvider } =
    useSubmitAttendanceForProvider();
  const { mutate: submitAttendanceForFamily } = useSubmitAttendanceForFamily();

  const [submitting, setSubmitting] = React.useState(false);
  const [submissionErrored, setSubmissionErrored] = React.useState(false);
  const onSubmit = (values: any) => {
    /*
     * Submission for a provider
     */
    if (isProviderView) {
      const _performSubmission = () => {
        const didAttendDuringBillingPeriod =
          values.attendedEntireMonth === true;

        const _days =
          attendanceSheet?.days?.map((day) => {
            if (!didAttendDuringBillingPeriod) {
              day.code = DayStatus.NO_CARE_PROVIDED;
              return day;
            }

            if (
              // if day is weekend
              dayjs(day.dateAttended).day() === 0 ||
              dayjs(day.dateAttended).day() === 6
            ) {
              day.code = DayStatus.NO_CARE_PROVIDED;
            } else if (
              // if day is same as termination date
              terminationDate &&
              dayjs(day.dateAttended).date() === terminationDate.date()
            ) {
              day.code = DayStatus.LAST_DAY;
            } else if (
              // if before care start date
              (careStartDateFormValue &&
                dayjs(day.dateAttended).isBefore(careStartDateFormValue)) ||
              // or after termination date
              (terminationDate &&
                dayjs(day.dateAttended).isAfter(terminationDate))
            ) {
              day.code = DayStatus.NO_CARE_PROVIDED;
            } else if (
              daysNotInCare?.[dayjs(day.dateAttended).date()] === true
            ) {
              day.code = DayStatus.NO_CARE_PROVIDED;
            } else {
              day.code = DayStatus.ATTENDED;
            }

            return day;
          }) || [];

        // Prepare payload
        const _payload: SubmitAttendanceForProviderDto = {
          childId: child?.id || -1,
          attendanceId: attendanceSheet?.id || -1,
          providerId: providerId || -1,
          platform: attendanceSheet?.platform || "",
          submittedBy: values.providerName,
          // Whether or not care was received during the billing period
          attendedEntireMonth: values.attendedEntireMonth,
          chargedForEntireMonth: didAttendDuringBillingPeriod
            ? values.chargedForMonth === false
              ? false
              : true
            : true,
          // If care was not received for the entire month, report all the days
          days: _days,
          // Termination date
          terminated: values.terminated,
          terminationDate: terminationDate
            ? terminationDate?.format("YYYY-MM-DD") || ""
            : "",
          // Billing statement
          amountBilled: didAttendDuringBillingPeriod
            ? values.amountBilled
            : undefined,
          // Care start
          careStartMonth: didAttendDuringBillingPeriod
            ? values.careStartMonth
            : false,
          careStartDate: didAttendDuringBillingPeriod
            ? values.careStartDate
            : "",
        };

        // Submit data to the server
        submitAttendanceForProvider(_payload, {
          onSuccess: () => {
            notification.success({
              message: "Attendance sheet submitted",
              description:
                "Once the family sponsor has submitted their agreement, a payment coordinator will review and issue payment accordingly.",
            });

            navigate(`/?${searchParams.toString()}`);
          },
          onError: (error) => {
            console.log("Error submitting", error);

            setSubmissionErrored(true);
          },
          onSettled: () => {
            setSubmitting(false);
          },
        });
      };

      setSubmitting(true);

      // Submit immediately if no file to upload or no care during billing period
      if (!fileToUpload || values.attendedEntireMonth === false) {
        _performSubmission();
      } else {
        const formData = new FormData();
        formData.append("billingStatement", fileToUpload);
        formData.append("platform", attendanceSheet?.platform || "");

        const config = {
          method: "post",
          maxBodyLength: Infinity,
          url: `${API_ENTRYPOINT}/attendance/${attendanceSheet?.id}/file`,
          data: formData,
        };

        axios
          .request(config)
          .then(() => {
            _performSubmission();
          })
          .catch((error) => {
            console.log("Error uploading file", error);

            setSubmissionErrored(true);
            setSubmitting(false);
          });
      }
    } else {
      /*
       * Submission for a parent
       */
      setSubmitting(true);

      // Prepare payload
      const _payload: SubmitAttendanceForFamilyDto = {
        familyId: familyId || -1,
        familyAccepted: values.parentInAgreementWithProviderDetails,
        declineReason: values.declineReason,
        attendanceId: attendanceSheet?.id || -1,
        childId: child?.id || -1,
        platform: attendanceSheet?.platform || "",
        submittedBy: values.parentName,
      };

      // Submit data to the server
      submitAttendanceForFamily(_payload, {
        onSuccess: () => {
          notification.success({
            message: "Submitted",
            description:
              "We will report this information back to your provider.",
          });

          navigate(`/?${searchParams.toString()}`);
        },
        onError: (error) => {
          console.log("Error submitting", error);

          setSubmissionErrored(true);
        },
        onSettled: () => {
          setSubmitting(false);
        },
      });
    }
  };

  const [daysNotInCare, setDaysNotInCare] = React.useState<{
    [key: number]: boolean;
  }>({});
  const [terminationDate, setTerminationDate] =
    React.useState<dayjs.Dayjs | null>(null);

  /*
   * Redirect to home if attendance sheet is not found
   * or if the attendance sheet is not editable
   */
  useEffect(() => {
    const _status = attendanceSheet?.status?.toLowerCase();
    const _isEditable = !(_status === "incomplete" && isFamilyView);
    if (!_isEditable) {
      navigate(`/?${searchParams.toString()}`);

      return;
    }
  }, [
    attendanceSheetLoading,
    attendanceSheet,
    isFamilyView,
    navigate,
    searchParams,
  ]);

  const _showProviderResubmissionUI =
    isProviderView && (attendanceSheet?.status || "") === "FAMILY_DENIED";

  const isReadOnly = isAttendanceReadOnly(
    attendanceSheet?.status as AttendanceStatus,
    isProviderView ? "provider" : "family",
    attendanceSheet
  );
  const isNoCareBillingPeriod =
    attendanceSheet?.status === AttendanceStatus.NO_CARE_BILLING_PERIOD;

  /*
   * If in re-submission mode, then we need to
   * show the originally filled out attendance sheet
   */
  useEffect(() => {
    if (attendanceSheet && _showProviderResubmissionUI) {
      const careStartDate = isValidDateString(attendanceSheet.careStartDate)
        ? parseDayMonthYearString(attendanceSheet.careStartDate)
        : null;
      const terminationDateValue = isValidDateString(
        attendanceSheet.terminationDate
      )
        ? parseDayMonthYearString(attendanceSheet.terminationDate)
        : null;

      /*
       * Seed only manually selected NC days — weekends, days before care
       * start, and days on/after termination are derived from current answers.
       */
      const _daysNotInCare: { [key: number]: boolean } = {};
      attendanceSheet?.days?.forEach((day) => {
        const date = dayjs(day.dateAttended);
        const isWeekend = date.day() === 0 || date.day() === 6;
        const isBeforeCareStart =
          careStartDate !== null && date.isBefore(careStartDate, "day");
        const isOnOrAfterTermination =
          terminationDateValue !== null &&
          !date.isBefore(terminationDateValue, "day");

        if (
          day.code === DayStatus.NO_CARE_PROVIDED &&
          !isWeekend &&
          !isBeforeCareStart &&
          !isOnOrAfterTermination
        ) {
          _daysNotInCare[date.date()] = true;
        }
      });
      setDaysNotInCare(_daysNotInCare);

      // Set form values
      const formValues: Record<string, unknown> = {
        attendedEntireMonth: attendanceSheet.attendedEntireMonth,
        terminated: attendanceSheet.termination,
        careStartMonth: attendanceSheet.careStartMonth,
      };

      if (careStartDate) {
        formValues.careStartDate = careStartDate;
      }

      // Termination date
      if (terminationDateValue) {
        setTerminationDate(terminationDateValue);
        formValues.terminationDate = terminationDateValue;
      }

      form.setFieldsValue(formValues);
    }
  }, [attendanceSheet, form, _showProviderResubmissionUI]);

  return (
    <Container className="!max-w-[1100px]">
      {attendanceSheetDetailsErrored || submissionErrored ? (
        <GeneralErrorState error={attendanceSheetDetailsError} />
      ) : !child ? (
        <Spin spinning />
      ) : (
        <>
          <Breadcrumb
            items={[
              {
                title: (
                  <Link to={`/?${searchParams.toString()}`}>
                    <HomeOutlined />
                    <span className="ml-2">Back to Home</span>
                  </Link>
                ),
              },
            ]}
            className="ml-1 mb-4"
          />

          <Form
            layout="vertical"
            form={form}
            onFinish={(values) => {
              onSubmit(values);
            }}
          >
            <Card
              title={`${child.name} (ID: ${child.id})`}
              styles={{
                body: {
                  padding: "0px",
                },
              }}
            />

            <ChildActiveCertificates
              childId={attendanceSheet?.child?.id}
              platform={platform || ""}
              providerId={
                isProviderView ? providerId : attendanceSheet?.provider?.id
              }
            />

            <AttendanceSummary attendanceSheet={attendanceSheet} />

            {isNoCareBillingPeriod &&
            attendanceSheet?.submittedViaPaper !== true ? (
              <Card
                title="Calendar"
                styles={{ body: { padding: "0px", overflow: "hidden" } }}
              >
                <div className="relative" style={{ padding: "24px" }}>
                  <ReadOnlyView
                    attendanceSheet={attendanceSheet}
                    noCareBillingPeriod
                  />
                </div>
              </Card>
            ) : (isReadOnly || isFamilyView) &&
              attendanceSheet?.attendedEntireMonth === true &&
              attendanceSheet?.chargedForMonth !== true &&
              attendanceSheet?.submittedViaPaper !== true ? (
              <Card
                title="Provider's submitted schedule"
                styles={{ body: { padding: "0px", overflow: "hidden" } }}
              >
                <div className="relative" style={{ padding: "24px" }}>
                  <ReadOnlyView attendanceSheet={attendanceSheet} />
                </div>
              </Card>
            ) : !isReadOnly && !isFamilyView ? (
              <>
                <Card
                  title="Attendance Sheet"
                  styles={{ body: { padding: "0px", overflow: "hidden" } }}
                >
                  <div className="relative" style={{ padding: "24px" }}>
                    {_showProviderResubmissionUI && (
                      <Alert
                        className="mb-8"
                        message={
                          <span className="font-bold">Needs re-submission</span>
                        }
                        description={
                          <>
                            <span>
                              This attendance sheet was rejected by the family.
                              Please edit below, and re-submit.
                            </span>

                            {attendanceSheet.declineReason && (
                              <>
                                <Divider />

                                <p className="font-bold">
                                  Reason family declined:
                                </p>
                                <p>{attendanceSheet.declineReason}</p>
                              </>
                            )}
                          </>
                        }
                        type="warning"
                        showIcon
                        closable
                      />
                    )}

                    {(isProviderView || terminationDate) && (
                      <div className="w-full py-12 xl:py-0">
                        {isProviderView ? (
                          <>
                            <Form.Item
                              label={
                                <span className="text-black font-bold">
                                  Did the child attend during the Billing Period{" "}
                                  {billingPeriodLabel}?
                                </span>
                              }
                              name="attendedEntireMonth"
                              rules={[
                                {
                                  required: true,
                                  message:
                                    "Please specify if the child attended during the billing period",
                                },
                              ]}
                            >
                              <Radio.Group
                                onChange={(e) => {
                                  if (e.target.value === false) {
                                    setDaysNotInCare({});
                                    setFileToUpload(null);
                                    form.setFieldsValue({
                                      careStartMonth: undefined,
                                      careStartDate: null,
                                      chargedForMonth: undefined,
                                      amountBilled: undefined,
                                    });
                                  }
                                }}
                              >
                                <Space direction="horizontal">
                                  <Radio value={true}>Yes</Radio>
                                  <Radio value={false}>No</Radio>
                                </Space>
                              </Radio.Group>
                            </Form.Item>

                            {attendedEntireMonthFormValue === true && (
                              <>
                                <Form.Item
                                  name="careStartMonth"
                                  label={
                                    <span className="text-black font-bold">
                                      Is this the child’s first month of care or
                                      first approval month at this location?
                                    </span>
                                  }
                                  rules={[
                                    {
                                      required: true,
                                      message:
                                        "Please specify whether this is the first month of care or first approval month at this location",
                                    },
                                  ]}
                                >
                                  <Radio.Group
                                    onChange={(e) => {
                                      if (e.target.value === false) {
                                        form.setFieldsValue({
                                          careStartDate: null,
                                        });
                                      }
                                    }}
                                  >
                                    <Space direction="horizontal">
                                      <Radio value={true}>Yes</Radio>
                                      <Radio value={false}>No</Radio>
                                    </Space>
                                  </Radio.Group>
                                </Form.Item>

                                {careStartMonthFormValue && (
                                  <Form.Item
                                    label={
                                      <span className="text-black font-bold">
                                        Start date
                                      </span>
                                    }
                                    name="careStartDate"
                                    rules={[
                                      {
                                        required: true,
                                        message:
                                          "Please specify the start date",
                                      },
                                      // can only be before the termination date
                                      {
                                        validator: (_, value) => {
                                          if (
                                            terminationDate &&
                                            value &&
                                            value.isAfter(terminationDate)
                                          ) {
                                            return Promise.reject(
                                              new Error(
                                                "Start date cannot be after the termination date"
                                              )
                                            );
                                          }

                                          return Promise.resolve();
                                        },
                                      },
                                    ]}
                                    dependencies={[["terminationDate"]]}
                                  >
                                    <DatePicker
                                      size="large"
                                      minDate={parseDayMonthYearString(
                                        startDate
                                      )}
                                      maxDate={parseDayMonthYearString(endDate)}
                                      // disable weekends
                                      disabledDate={(day) =>
                                        day.day() === 0 || day.day() === 6
                                      }
                                    />
                                  </Form.Item>
                                )}
                              </>
                            )}

                            <Form.Item
                              label={
                                <span className="text-black font-bold inline-flex items-center gap-1">
                                  Was care terminated during the Billing Period{" "}
                                  {billingPeriodLabel}?
                                  <Tooltip title="MCCYN Fee Assistance Programs do not cover fees beyond the child’s last day in care.">
                                    <InformationFilled className="cursor-pointer" />
                                  </Tooltip>
                                </span>
                              }
                              name="terminated"
                              rules={[
                                {
                                  required: true,
                                  message:
                                    "Please specify if care was terminated",
                                },
                              ]}
                            >
                              <Radio.Group
                                onChange={(e) => {
                                  if (e.target.value === false) {
                                    setTerminationDate(null);
                                    form.setFieldsValue({
                                      terminationDate: null,
                                    });
                                  }
                                }}
                              >
                                <Space direction="horizontal">
                                  <Radio value={true}>Yes</Radio>
                                  <Radio value={false}>No</Radio>
                                </Space>
                              </Radio.Group>
                            </Form.Item>
                          </>
                        ) : (
                          <p className="text-lg font-semibold tracking-normal mb-6">
                            Termination date
                          </p>
                        )}

                        {terminatedFormValue && (
                          <Form.Item
                            name="terminationDate"
                            label={
                              isProviderView ? (
                                <span className="text-black font-bold">
                                  Termination date
                                </span>
                              ) : undefined
                            }
                            rules={[
                              {
                                required: true,
                                message: "Please select a termination date",
                              },
                              // can only be after the care start date
                              {
                                validator: (_, value) => {
                                  if (
                                    careStartDateFormValue &&
                                    value &&
                                    value.isBefore(careStartDateFormValue)
                                  ) {
                                    return Promise.reject(
                                      new Error(
                                        "Termination date cannot be before the care start date"
                                      )
                                    );
                                  }

                                  return Promise.resolve();
                                },
                              },
                            ]}
                            dependencies={[["careStartDate"]]}
                          >
                            <DatePicker
                              size="large"
                              onChange={(value) => {
                                setTerminationDate(value);
                              }}
                              minDate={parseDayMonthYearString(startDate)}
                              maxDate={parseDayMonthYearString(endDate)}
                              defaultPickerValue={parseDayMonthYearString(
                                endDate
                              )}
                              // disable weekends
                              disabledDate={(day) =>
                                day.day() === 0 || day.day() === 6
                              }
                              style={{ minWidth: "270px" }}
                            />
                          </Form.Item>
                        )}
                      </div>
                    )}
                  </div>
                </Card>

                {attendedEntireMonthFormValue === true ? (
                  <EasScheduleForm
                    attendaceSheet={attendanceSheet}
                    child={child}
                    daysNotInCare={daysNotInCare}
                    setDaysNotInCare={setDaysNotInCare}
                    terminationDate={terminationDate}
                    setTerminationDate={setTerminationDate}
                    fileToUpload={fileToUpload}
                    setFileToUpload={setFileToUpload}
                    showProviderResubmissionUI={_showProviderResubmissionUI}
                  />
                ) : (
                  <Divider className="my-12" />
                )}
              </>
            ) : null}

            {isReadOnly ? (
              <AttendanceFooter
                isReadOnly={isReadOnly}
                searchParams={searchParams}
                submitting={submitting}
              />
            ) : (
              <Card
                title={
                  !isReadOnly
                    ? isProviderView
                      ? "FINAL ACKNOWLEDGEMENT"
                      : "Your Response"
                    : undefined
                }
                styles={{ body: { padding: "0px", overflow: "hidden" } }}
                className="mt-10"
              >
                {!isReadOnly && (
                  <div className="relative" style={{ padding: "24px" }}>
                    <EasAcknowledgements
                      attendanceSheet={attendanceSheet}
                      showProviderResubmissionUI={_showProviderResubmissionUI}
                    />
                  </div>
                )}

                <AttendanceFooter
                  isReadOnly={isReadOnly}
                  searchParams={searchParams}
                  submitting={submitting}
                />
              </Card>
            )}
          </Form>
        </>
      )}
    </Container>
  );
}

function AttendanceFooter({
  isReadOnly,
  searchParams,
  submitting,
}: {
  isReadOnly: string | boolean | null | undefined;
  searchParams: URLSearchParams;
  submitting: boolean;
}) {
  const form = Form.useFormInstance();

  return (
    <Footer
      className={`bg-black bg-opacity-10 ${
        isReadOnly ? "rounded-lg mt-6" : "rounded-b-lg"
      } flex items-center`}
      style={{ textAlign: "center", padding: "24px" }}
    >
      <Link to={`/?${searchParams.toString()}`} className="text-current">
        <CaretLeftOutlined className="mr-2" />
        Back
      </Link>

      <div className="flex-grow" />

      {!isReadOnly && (
        <Button
          type="primary"
          onClick={() => {
            form.submit();
          }}
          loading={submitting}
        >
          Submit <CheckOutlined />
        </Button>
      )}
    </Footer>
  );
}
